import { Values } from '@utils-global/type-values';
import { ModelBase } from '@models-global/model-base';
import { ENUM_ENTITY_STATUS } from '@enums-global/enum-entity-status';
import { parseDateString } from '@utils-global/date-time';
import { ModelAgreementCustomer } from '@module-agreement-customer/models/model-agreement-customer';
import { AuditCustomerType } from '@module-customer/enums/audit-customer-type.enum';
import { AuditServiceMethod } from '@module-customer/enums/audit-service-method.enum';
import { AuditServiceScope } from '@module-customer/enums/audit-service-scope.enum';
import { CustomerServiceType } from '@module-customer/enums/customer-service-type.enum';
import { CoveredEntityAccount } from '@module-covered-entity/models/ce-account';

export interface Customer {
  readonly address: string;
  readonly city: string;
  readonly zip: string;
  readonly code: string | null;
  readonly bin?: string | null;
  readonly createdAt: Date | string;
  readonly id: string | null;
  readonly mailOrderPharmacyList?: string | null;
  readonly name: string;
  readonly numberOfLives?: string | null;
  readonly pcn?: string | null;
  readonly phoneNumber: string;
  readonly processor?: string | null;
  readonly status: Values<typeof ENUM_ENTITY_STATUS>;
  readonly taxId?: string | null;
  readonly updatedAt: Date | string;
  readonly website: string;
  readonly isNew: boolean;
  readonly numberOfHealthPlans: number;
  readonly year: Date;
  readonly agreement?: ModelAgreementCustomer;
  readonly serviceType: CustomerServiceType;
  readonly auditCustomerType?: AuditCustomerType;
  readonly auditResubmission?: boolean;
  readonly auditServiceScope?: AuditServiceScope;
  readonly auditServiceMethod?: AuditServiceMethod;
  readonly contactName?: string | null;
  readonly email?: string | null;
  readonly publicKey?: string | null;
  readonly hostManagedMFPPricing?: boolean | null;
  readonly containerName?: string | null;
  readonly CustomerToCoveredEntityAccount?: {
    coveredEntityAccount: Partial<CoveredEntityAccount>;
  }[];
}

export class ModelCustomer
  implements ModelBase<Customer, ModelCustomer>, Customer
{
  readonly address: string;
  readonly city: string;
  readonly zip: string;
  readonly code: string | null;
  readonly bin?: string | null;
  readonly createdAt: string | Date;
  readonly id: string | null;
  readonly mailOrderPharmacyList?: string | null;
  readonly name: string;
  readonly numberOfLives?: string | null;
  readonly pcn?: string | null;
  readonly phoneNumber: string;
  readonly processor?: string | null;
  readonly status: Values<typeof ENUM_ENTITY_STATUS>;
  readonly taxId?: string | null;
  readonly updatedAt: string | Date;
  readonly website: string;
  readonly isNew: boolean;
  readonly numberOfHealthPlans: number;
  readonly year: Date;
  readonly agreement?: ModelAgreementCustomer;
  readonly serviceType: CustomerServiceType;
  readonly auditCustomerType?: AuditCustomerType;
  readonly auditResubmission?: boolean;
  readonly auditServiceScope?: AuditServiceScope;
  readonly auditServiceMethod?: AuditServiceMethod;
  readonly contactName?: string | null;
  readonly email?: string | null;
  readonly publicKey?: string | null;
  readonly hostManagedMFPPricing?: boolean | null;
  readonly containerName?: string | null;
  readonly CustomerToCoveredEntityAccount?: {
    coveredEntityAccount: Partial<CoveredEntityAccount>;
  }[];

  constructor({
    id = null,
    code = null,
    bin = undefined,
    createdAt = new Date(),
    mailOrderPharmacyList = undefined,
    name = '',
    address = '',
    city = '',
    zip = '',
    isNew = true,
    numberOfLives = undefined,
    pcn = undefined,
    phoneNumber = '',
    processor = undefined,
    status = ENUM_ENTITY_STATUS.ACTIVE,
    taxId = undefined,
    updatedAt = new Date(),
    website = '',
    numberOfHealthPlans = 0,
    year = new Date(),
    agreement = undefined,
    serviceType = CustomerServiceType.REBATE_OPERATIONS,
    auditCustomerType = undefined,
    auditResubmission = undefined,
    auditServiceScope = undefined,
    auditServiceMethod = undefined,
    contactName = undefined,
    email = undefined,
    publicKey = undefined,
    hostManagedMFPPricing = undefined,
    containerName = undefined,
    CustomerToCoveredEntityAccount = undefined
  }: Partial<Customer> = {}) {
    this.id = id;
    this.code = code;
    this.bin = bin;
    this.createdAt = parseDateString(createdAt);
    this.mailOrderPharmacyList = mailOrderPharmacyList;
    this.name = name;
    this.address = address;
    this.city = city;
    this.zip = zip;
    this.isNew = isNew;
    this.numberOfLives = numberOfLives;
    this.pcn = pcn;
    this.phoneNumber = phoneNumber;
    this.processor = processor;
    this.status = status;
    this.taxId = taxId;
    this.updatedAt = updatedAt;
    this.website = website;
    this.numberOfHealthPlans = numberOfHealthPlans;
    this.year = parseDateString(year);
    this.agreement = agreement;
    this.serviceType = serviceType;
    this.auditCustomerType = auditCustomerType;
    this.auditResubmission = auditResubmission;
    this.auditServiceScope = auditServiceScope;
    this.auditServiceMethod = auditServiceMethod;
    this.contactName = contactName;
    this.email = email?.toLowerCase();
    this.publicKey = publicKey;
    this.hostManagedMFPPricing = hostManagedMFPPricing;
    this.containerName = containerName;
    this.CustomerToCoveredEntityAccount = CustomerToCoveredEntityAccount;
  }

  serialize(): Customer {
    return {
      id: this.id,
      code: this.code,
      bin: this.bin,
      createdAt: this.createdAt,
      mailOrderPharmacyList: this.mailOrderPharmacyList,
      name: this.name,
      address: this.address,
      city: this.city,
      zip: this.zip,
      isNew: this.isNew,
      numberOfLives: this.numberOfLives,
      pcn: this.pcn,
      phoneNumber: this.phoneNumber,
      processor: this.processor,
      status: this.status,
      taxId: this.taxId,
      updatedAt: this.updatedAt,
      website: this.website,
      numberOfHealthPlans: this.numberOfHealthPlans,
      year: this.year,
      serviceType: this.serviceType,
      auditCustomerType: this.auditCustomerType,
      auditResubmission: this.auditResubmission,
      auditServiceScope: this.auditServiceScope,
      auditServiceMethod: this.auditServiceMethod,
      contactName: this.contactName,
      email: this.email,
      publicKey: this.publicKey,
      hostManagedMFPPricing: this.hostManagedMFPPricing
    };
  }

  serializeToServer(): Customer {
    const override = {
      id: this.isNew ? null : this.id,
      code: this.isNew ? null : this.code,
      isNew: false
    };
    return { ...this.serialize(), ...override };
  }

  clone(): ModelCustomer {
    return new ModelCustomer(this.serialize());
  }
}
